import React, { useState, useEffect, useRef } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { Input, InputProps } from '@mantine/core';
import { DEFAULT_ADDRESS } from '@constants/jobInitialSearch';
import { isBrowserOutdated } from '@utils/BrowserUtils';
import { GoSearch } from 'react-icons/go';

interface MapsAutoCompleteProps extends Omit<InputProps, 'onChange'> {
  defaultAddress?: string;
  value?: string;
  setSelectedPlace?: (
    selectedPlace: google.maps.places.PlaceResult | null
  ) => void;
  setInputValue?: (value: string) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
}

const countryRestriction = ['US', 'CA'];

function MapsAutoCompleteV2({
  defaultAddress,
  value,
  setInputValue,
  setSelectedPlace,
  ...props
}: MapsAutoCompleteProps) {
  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary('places');

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      componentRestrictions: { country: countryRestriction },
      fields: [
        'address_components',
        'formatted_address',
        'geometry',
        'icon',
        'name',
      ],
    };

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
  }, [places]);

  useEffect(() => {
    if (!placeAutocomplete) return;

    if (isBrowserOutdated()) {
      console.warn('Safari version is outdated');
      return;
    }

    const listener = placeAutocomplete.addListener('place_changed', () => {
      setSelectedPlace?.(placeAutocomplete.getPlace());
    });

    // eslint-disable-next-line consistent-return
    return () => {
      listener.remove();
    };
  }, [setSelectedPlace, placeAutocomplete]);

  // Use controlled value if provided, otherwise fall back to defaultAddress
  const inputValue =
    value !== undefined ? value : defaultAddress || DEFAULT_ADDRESS || '';

  return (
    <Input
      size="md"
      ref={inputRef}
      leftSection={<GoSearch size={20} />}
      value={inputValue}
      onChange={(e) => {
        setInputValue?.(e.target.value);
        props.onChange?.(e);
      }}
      {...props}
    />
  );
}

export default MapsAutoCompleteV2;
